import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { get, uniqBy, uniqueId } from 'lodash';
import { themeAlpine } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';

import { setLoading, useApplicationState, setPartnerProjectSettings } from '../../utilities/applicationState';
import { ListPlaceHolder } from '../common/placeholder';
import {
  SdfBadge,
  SdfBusyIndicator,
  SdfButton,
  SdfFocusPane,
  SdfGrid,
  SdfIcon,
  SdfInput,
  SdfMultiSelect,
  SdfSelect,
  SdfShimmer,
  SdfTag,
  SdfTooltip,
  SdfxPagination
} from '@synerg/react-components';
import './projectListTable.scss';
import Toast from '../common/toast';
import ImportLegacyProject from '../importLegacyProject/importLegacyProject';

const agGridKey = window.SynergConfig.getKey('ag-grid-enterprise');
LicenseManager.setLicenseKey(agGridKey);

export default function ProjectListTable() {
  const [appState, dispatch] = useApplicationState();
  const partnerProjectsSettings = appState.partnerProjectSettings || {};
  const ref = useRef();
  const grid = useRef();
  const itemCountRef = useRef(10);
  const scrollYRef = useRef(0);
  const listBoxEl = useRef(null);
  const prevItemCountRef = useRef(10);
  const [isBusy, setIsBusy] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [totalData, setTotalData] = useState('');
  const [reportDownloading, setReportDownloading] = useState(false);
  const [projectReport, setProjectReport] = useState(false);
  const [toast, setToast] = useState(false);
  const [showSlideInModal, setShowSlideInModal] = useState(false);

  // available filter
  const [filterAppOrPartner, setFilterAppOrPartner] = useState(partnerProjectsSettings.filterAppOrPartner || '');
  const [filterStatus, setFilterStatus] = useState(partnerProjectsSettings.filterStatus || []);
  const [filterTechAdvisor, setFilterTechAdvisor] = useState(partnerProjectsSettings.filterTechAdvisor || []);
  const [advisors, setAdvisors] = useState([]);
  const [showColumns, setShowColumns] = useState(false);

  // Sort state
  const [sortOrder, setSortOrder] = useState(partnerProjectsSettings.sortOrder || {
    colId: 'createdDate',
    sortBy: -1
  });

  // eslint-disable-next-line no-unused-vars
  const [status, setStatus] = useState([
    { label: 'Not Started', value: 'Not Started' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Pre Launch', value: 'Pre Launch' },
    { label: 'Live', value: 'Live' },
    { label: 'Legacy', value: 'Legacy' }
  ]);

  const [selectedPaginationOptions, setSelectedPaginationOptions] = useState(partnerProjectsSettings.pagination || {
    totalPages: 1,
    pageSize: 10,
    page: 1,
    skip: 0
  });
  const [start, setStart] = useState(1);
  const [end, setEnd] = useState(10);
  const [onChangePageSize, setOnChangePageSize] = useState(10);
  const [onChangePage, setOnChangePage] = useState(1);
  const [cancelableRequest, setCancelableRequest] = useState(false);
  const [filterChange, setFilterChange] = useState(null);

  const history = useHistory();

  const myIcons = {
    first: "<sdf-icon icon='nav-page-first' />",
    previous: "<sdf-icon icon='nav-page-back' />",
    next: "<sdf-icon icon='nav-page-next' />",
    last: "<sdf-icon icon='nav-page-last' />",
    sortAscending: "<sdf-icon icon='action-sort-asc' />",
    sortDescending: "<sdf-icon icon='action-sort-desc' />",
    sortUnSort: "<sdf-icon icon='action-sort' />"
  };

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: false,
    wrapText: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    unSortIcon: true,
    cellDataType: false,
    headerClass: 'font-bold'
  };

  const gridOptions = {
    defaultColDef: defaultColDef,
    pagination: false,
    suppressPaginationPanel: true,
    paginationPageSize: 10,
    suppressHorizontalScroll: true,
    theme: themeAlpine
  };

  const current = appState.projects.current || {};
  const updatedProjectName = get(current, 'devInfo.updatedProjectName', false);

  const STATE = {
    "Not Started": 'failed',
    "In Progress": 'in-progress',
    "Pre Launch": 'new',
    "Live": 'complete',
    "Legacy": 'default'
  };

  const SORT_BY = {
    "projectName": 'orderByName',
    "startDate": 'orderByStartDate',
    "lastLogin": 'orderByLastLogin',
    "createdDate": 'orderByCreatedDate',
    "technicalAdvisor": 'orderByTechnicalAdvisor',
    "businessAdvisor": 'orderByBusinessAdvisor',
    "partnerAdvisor": 'orderByPartnerAdvisor',
    "partnerType": 'orderByPartnerType',
    "adpProduct": 'orderByAdpProduct',
    "caseDisplay": 'orderByUseCase',
    "mkpllocal": 'orderByMkplLocal'
  };

  const handleResponse = (message, status = "error") => {
    setToast({
      key: uniqueId(),
      status: status,
      message: message,
      showToast: true
    });
  };

  const updateStartAndEnd = (total, pageNo, pageSize) => {
    setStart(total > 0 ? (pageNo - 1) * pageSize + 1 : 0);
    setEnd((pageNo * pageSize) > total ? total : (pageNo * pageSize));
  };

  const getCancelableRequestToken = () => {
    if (cancelableRequest) {
      cancelableRequest.cancel('Search request cancelled');
    }
    const newRequest = axios.CancelToken.source();
    setCancelableRequest(newRequest);
    return newRequest;
  };

  const search = async () => {
    try {
      dispatch(setLoading(true));
      const axiosInstance = axios.create();
      const { data: { projectsList, total, options } } = await axiosInstance.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/projects/search`, {
        cancelToken: getCancelableRequestToken().token,
        params: getSearchParams()
      });
      const advisorList = [...options.filters.technicalAdvisor, ...options.filters.businessAdvisor, ...options.filters.partnerAdvisor];
      if (advisors.length <= 0) {
        setAdvisors(advisorList.map(item => ({ key: item, 'label': item, 'value': item })));
      }
      setTableData(projectsList);
      setSelectedPaginationOptions({
        totalPages: Math.ceil(total / options.pageSize),
        skip: options.skipped,
        pageSize: options.pageSize || 10,
        page: Math.ceil(options.skipped / options.pageSize + 1) || 1,
        pagination: options.pagination.map(item => { return { label: item, value: item } }),
      });
      updateStartAndEnd(total, Math.ceil(options.skipped / options.pageSize) + 1, options.pageSize);
      setTotalData(total);
      setIsBusy(false);
      dispatch(setLoading(false));
    } catch (e) {
      if (!axios.isCancel(e)) {
        const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get Project List. Please try again.');
        handleResponse(errorMessage);
        setIsBusy(false);
        dispatch(setLoading(false));
      }
    }
  };

  const getSearchParams = (limit) => {
    const params = {
      skip: selectedPaginationOptions.skip,
      limit: limit === undefined ? selectedPaginationOptions.pageSize : limit,
      filterAppOrPartner: filterAppOrPartner.trim(),
      filterStatus: filterStatus.map(item => item.value),
      filterTechAdvisor: filterTechAdvisor.map(item => item.value),
      ...({
        [SORT_BY[sortOrder.colId]]: sortOrder.sortBy
      })
    };

    return Object.keys(params).reduce((prev, key) => {
      if (params[key] || params[key] === 0) {
        prev[key] = params[key];
      }
      return prev;
    }, {});
  };

  const ProjectInfoCell = (props) => {
    return (
      <div className={"flex justify-between"}>
        <div className="w-8/12">
          <div className={"text-sm leading-none"} title={props.data.projectName}>
            {props.data.projectName || ''}
          </div>
          <div className={"text-sm leading-lg text-tertiary"}>
            {props.data.partnerOrgName || ''}
          </div>
        </div>
        <div className="flex-1 text-center">
          <SdfBadge status={STATE[props.data.status]}>{props.data.status}</SdfBadge>
        </div>
      </div>
    );
  };

  const UseCaseCell = (props) => {
    return (
      <SdfTooltip trigger="hover" state="neutral" tooltipId={uniqueId()} attachmentPoint={'right-end'} portalEnabled={'true'}>
        <div slot='tooltip-target' className='overflow-ellipsis overflow-hidden'>{props.data.caseDisplay}</div>
        <div>{props.data.caseDisplay}</div>
      </SdfTooltip>
    );
  };

  const ViewLink = (props) => {
    const history = useHistory();
    return (
      <div>
        <SdfButton emphasis={"tertiary"} className={"whitespace-nowrap"}
                   onClick={() => {
                     history.push({ pathname: `/partner-projects/${props.data.projectAppId}`, state: props.data })
                   }}
        >
          View
        </SdfButton>
      </div>
    );
  };

  const determineSortOrder = (sortByCol) => {
    const determineAscOrDesc = () => {
      const retAscOrDesc = sortOrder.sortBy;
      if (retAscOrDesc === 1) {
        return 'asc';
      } else if (retAscOrDesc === -1) {
        return 'desc';
      } else {
        return null;
      }
    };

    switch (sortByCol) {
      case SORT_BY[sortOrder.colId]:
        return determineAscOrDesc();
      default:
        return null;
    }
  };

  const columnDefs = useMemo(() => [
    {
      minWidth: 400,
      field: "projectName",
      headerName: "Project Name/Partner Name",
      sortable: true,
      sort: determineSortOrder('orderByName'),
      suppressMovable: true,
      cellRenderer: (event) => ProjectInfoCell(event),
      cellStyle: () => ({
        justifyContent: "center",
        display: "flex",
        flexDirection: "column"
      }),
      pinned: 'left',
      toolPanelClass: "p-10",
      wrapText: true,
      autoHeight: true,
      suppressColumnsToolPanel: true,
      comparator: () => 0  // Disable client-side sorting
    },
    {
      field: "createdDate",
      headerName: "Creation Date",
      sortable: true,
      sort: determineSortOrder('orderByCreatedDate'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "startDate",
      headerName: "Start Date",
      sortable: true,
      sort: determineSortOrder('orderByStartDate'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "lastLogin",
      headerName: "Last Login",
      sortable: true,
      sort: determineSortOrder('orderByLastLogin'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "partnerType",
      headerName: "Partner type",
      sortable: true,
      sort: determineSortOrder('orderByPartnerType'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "technicalAdvisor",
      headerName: "Technical advisor",
      sortable: true,
      sort: determineSortOrder('orderByTechnicalAdvisor'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "businessAdvisor",
      headerName: "Business Advisor",
      sortable: true,
      sort: determineSortOrder('orderByBusinessAdvisor'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "partnerAdvisor",
      headerName: "Partner Advisor",
      sortable: true,
      sort: determineSortOrder('orderByPartnerAdvisor'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      suppressFillHandle: true,
      comparator: () => 0
    },
    {
      field: "adpProduct",
      headerName: "ADP product",
      sortable: true,
      sort: determineSortOrder('orderByAdpProduct'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "caseDisplay",
      headerName: "Use Case",
      sortable: true,
      sort: determineSortOrder('orderByUseCase'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      cellRenderer: (event) => UseCaseCell(event),
      comparator: () => 0
    },
    {
      field: "mkpllocal",
      headerName: "Marketplace locale",
      sortable: true,
      sort: determineSortOrder('orderByMkplLocal'),
      suppressMovable: true,
      toolPanelClass: "toolpanel",
      comparator: () => 0
    },
    {
      field: "view",
      headerName: "",
      sortable: false,
      suppressMovable: true,
      cellRenderer: (event) => ViewLink(event),
      suppressColumnsToolPanel: true,
      pinned: 'right',
      cellStyle: { textAlign: 'center' }
    }
  ], [sortOrder]);

  const clearPartnerProjectSettings = () => {
    return history.block((location) => {
      const pathName = get(location, 'pathname', '');
      const regex = /\/partner-projects\/[a-zA-Z0-9]+/;
      if (regex.test(pathName) || pathName === '/partner-projects') {
        dispatch(setPartnerProjectSettings({
          filterAppOrPartner: filterAppOrPartner.trim(),
          filterStatus: filterStatus,
          filterTechAdvisor: filterTechAdvisor,
          sortOrder: {
            colId: sortOrder.colId,
            sortBy: sortOrder.sortBy
          },
          pagination: selectedPaginationOptions
        }));
      } else {
        dispatch(setPartnerProjectSettings({
          filterAppOrPartner: '',
          filterStatus: '',
          filterTechAdvisor: '',
          sortOrder: { colId: 'createdDate', sortBy: -1 },
          pagination: { page: 1, pageSize: 10, skip: 0 }
        }));
      }
      return true;
    });
  }

  const stopDownloading = async () => {
    reportDownloading && getCancelableRequestToken();
    setShowDownloadModal(false);
    setProjectReport(false);
  };

  const downloadFile = async () => {
    const url = window.URL.createObjectURL(new Blob([projectReport.data]));
    const link = document.createElement('a');
    link.href = url;
    let headerLine = projectReport.headers["content-disposition"];
    var match = headerLine.match(/filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i);
    var filename = match[3];
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    stopDownloading();
  };

  const downloadReport = async () => {
    setReportDownloading(true);
    const axiosInstance = axios.create();
    await axiosInstance.get(`${import.meta.env.VITE_ROOT_PATH}/api/partner/projects/download`, {
      cancelToken: getCancelableRequestToken().token,
      params: getSearchParams(),
      responseType: 'blob',
    }).then(response => {
      setProjectReport(response);
    }).catch(error => {
      if (!axios.isCancel(error)) {
        const errorMessage = get(error, 'response.data.message', 'Error downloading project report.');
        stopDownloading();
        handleResponse(errorMessage);
      }
    });
    setReportDownloading(false);
  };

  const sideBar = useMemo(() => ({
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressSyncLayoutWithGrid: true,
          suppressColumnMove: true,
          suppressColumnFilter: true,
          suppressColumnSelectAll: true,
          suppressColumnExpandAll: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressRowGroups: true,
          suppressValues: true,
        }
      }
    ],
    position: 'right',
    hiddenByDefault: true  // This hides it initially
  }), []);

  const setSideBarVisible = useCallback(() => {
    if (!grid.current || !grid.current.api) {
      return;
    }

    const api = grid.current.api;

    if (showColumns) {
      api.setSideBarVisible(false);
      setShowColumns(false);
    } else {
      api.setSideBarVisible(true);
      api.openToolPanel('columns');
      setShowColumns(true);
    }
  }, [showColumns]);

  const hasNoCriteria = () => {
    return [
      filterAppOrPartner.trim(),
      filterTechAdvisor,
      filterStatus
    ].every(f => !f || (Array.isArray(f) && f.length === 0));
  };

  const removeTag = (tag) => {
    const updatedAdvisors = filterTechAdvisor.filter((adv) => adv !== tag);
    const updatedStatus = filterStatus.filter((status) => status !== tag);
    updateFilterTechAdvisor(updatedAdvisors);
    updateFilterStatus(updatedStatus);
  };

  const clearAllButton = () => {
    setSelectedPaginationOptions((prev) => {
      return { ...prev, skip: 0, page: 1 }
    });
    updateFilterTechAdvisor([]);
    updateFilterStatus([]);
  };

  const updateFilterAppOrPartner = v => setFilterAppOrPartner(v) || updateFilter();
  const updateFilterTechAdvisor = v => {
    setFilterTechAdvisor(v) || updateFilter();
  }
  const updateFilterStatus = v => setFilterStatus(v) || updateFilter();
  const updateFilter = () => resetPagination() || setFilterChange(Date.now());
  const resetPagination = () => setSelectedPaginationOptions({ skip: 0, page: 1, pageSize: selectedPaginationOptions.pageSize });

  // Fix for dropdown not closing on click.
  useEffect(() => {
    const documentClickListener = event => {
      if (!listBoxEl.current) {
        return;
      }
      const dropdownDOM = get(listBoxEl, 'current._input.parentNode');
      if (!dropdownDOM || dropdownDOM.contains(event.target)) {
        return;
      }
      listBoxEl.current.close();
    };
    document.addEventListener('mousedown', documentClickListener);
    return function cleanup() {
      document.removeEventListener('mousedown', documentClickListener);
    };
  }, []);

  // search on state update - RESTORED sortOrder dependency
  // FIX: Prevent infinite loop by tracking previous sortOrder
  const prevSortOrderRef = useRef(sortOrder);

  useEffect(() => {
    const sortChanged = prevSortOrderRef.current.colId !== sortOrder.colId ||
      prevSortOrderRef.current.sortBy !== sortOrder.sortBy;

    if (filterChange || onChangePageSize || onChangePage || sortChanged) {
      search();
    }

    prevSortOrderRef.current = sortOrder;

    const clearSettings = clearPartnerProjectSettings();

    return ()=> {
      clearSettings();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterChange, sortOrder, onChangePageSize, onChangePage]);

  useEffect(() => {
    if (updatedProjectName) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProjectName]);

  useEffect(() => {
    if (showDownloadModal) {
      downloadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDownloadModal]);

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //safari scroll issue
    if (prevItemCountRef.current < itemCountRef.current) {
      prevItemCountRef.current = itemCountRef.current;
      window.scrollTo(0, scrollYRef.current);
    } else if (prevItemCountRef.current > itemCountRef.current) {
      prevItemCountRef.current = itemCountRef.current;
      scrollYRef.current = window.scrollY;
    }
  });

  return (
    <div className="project-table-box">
      {toast && <Toast
        key={toast.key}
        status={toast.status}
        message={toast.message}
        showToast={toast.showToast}
      />}
      {showDownloadModal && <SdfFocusPane
        paneType="floating"
        visible={true}
        size="small"
        onSdfDismiss={() => stopDownloading()}
        closeable={true}
        hideAcceptButton={true}
        hideDismissButton={true}
      >
        <div slot="title" className="text-heading-05">Download report</div>
        <div className="px-4 text-body-02">
          {projectReport ? 'Report successfully generated, click the download button to save the file.' : 'Report is generating. Please wait....'}
        </div>

        <div slot={"custom-buttons"} className="mt-3">
          <SdfButton onClick={() => stopDownloading()}>
            Cancel
          </SdfButton>
          <SdfButton
            emphasis="primary"
            onClick={() => downloadFile()}
            className="ml-3"
            {...(!projectReport) && { disabled: true }}
          >
            <div style={{ height: '1.625rem' }}>
              Download
              {reportDownloading && <SdfBusyIndicator className='relative' style={{ bottom: '1.25rem' }} size='sm' />}
            </div>
          </SdfButton>
        </div>
      </SdfFocusPane>}
      <div className={"flex pt-10 pb-5 mx-0 justify-between"}>
        <span className={"block w-lg text-3xl font-extrabold leading-xs tracking-xs"}>Projects</span>
        <div>
          <SdfButton emphasis={"secondary"} onClick={() => setShowSlideInModal(true)}>Import legacy projects</SdfButton>
          <SdfButton icon="action-download" className="ml-5" onClick={() => { ref.current && setShowDownloadModal(true) }} emphasis={"primary"}>
            <a href="#!" id="download-table-csv-anchor" style={{ display: 'none' }}>Download report</a>
            Download report
          </SdfButton>
        </div>
      </div>
      {appState.loading && <div className="project-list-load">
        <SdfBusyIndicator
          style={{ color: "#1a8099" }}
          size="xl"
          fullScreen={true}
        />
      </div>}
      {isBusy && <ListPlaceHolder filtersNumber={4} className="placeholder-table" style={{ height: '350px' }} />}
      {!isBusy && <div ref={ref} className="project-list-container">
        <div className="siblings">
          <div className={"bg-action-default-50 my-5 rounded-xl"}>
            <div className={"p-10 pb-5 px-12"}>
              <div className={"flex justify-between gap-5"} style={{ height: "4rem" }}>
                <SdfInput
                  className={"mr-6 w-1/2"}
                  label={"Search"}
                  placeholder={"Search by project name, partner name, consumer app ID"}
                  value={filterAppOrPartner}
                  onSdfInput={($event) => {
                    updateFilterAppOrPartner($event.detail)
                  }}
                >
                  <SdfIcon slot={"after"} icon={"action-search"} />
                </SdfInput>

                <div className="textbox-with-label">
                  <SdfMultiSelect
                    label={"Filter by Status"}
                    className="min-w-60"
                    items={status}
                    value={filterStatus}
                    placeholder="Select status"
                    input_width={"xl"}
                    onSdfChange={({ detail }) => { updateFilterStatus(detail) }}
                  />
                </div>
                <div className="textbox-with-label">
                  <SdfMultiSelect
                    label={"Filter by advisor"}
                    className="min-w-60"
                    items={advisors}
                    filterable={true}
                    value={filterTechAdvisor}
                    onSdfChange={({ detail }) => { updateFilterTechAdvisor(detail) }}
                    placeholder="Select advisor"
                    defaultSearchTerm=""
                  />
                </div>
              </div>
            </div>
            <div className={"flex flex-wrap tags-section pl-12 pb-10"}>
              {[...filterTechAdvisor, ...filterStatus].map((option, idx) => {
                return <SdfTag
                  key={`tag-${idx}`}
                  className="bg-white rounded-full mr-2 mt-4"
                  onClick={() => removeTag(option)}
                  value={option.value}
                />
              })}
              {[...filterTechAdvisor, ...filterStatus].length > 0 &&
                <SdfButton onClick={clearAllButton} className={'ml-2 mt-4'} variant={'text'}>Clear all</SdfButton>
              }
            </div>
          </div>
        </div>
        {
          (tableData.length > 0 || appState.loading) &&
          <div>
            <div className={"flex my-4 mx-0 justify-between"}>
              <div className={"block w-lg text-3xl font-extrabold leading-xs tracking-xs"}>Results ({totalData})</div>
              <div onClick={() => {
                console.log('Wrapper clicked, grid ref:', grid.current);
                setSideBarVisible();
              }}>
                <SdfButton
                  emphasis="tertiary"
                  variant="text"
                  icon={showColumns ? "action-hide" : "action-show"}
                >
                  &nbsp;Show/Hide Columns
                </SdfButton>
              </div>
            </div>
            <div className="ag-grid-container">
              <SdfGrid className={"ag-theme-alpine"}>
                <AgGridReact
                  ref={grid}
                  rowHeight={60}
                  headerHeight={59}
                  domLayout='autoHeight'  // Changed back to autoHeight
                  gridOptions={gridOptions}
                  icons={myIcons}
                  rowData={tableData}
                  rowClass={"template-row pt-1 cursor-pointer"}
                  autoSizeAllColumns={true}
                  sideBar={sideBar}
                  columnDefs={columnDefs}
                  onGridReady={(params) => {
                    console.log('Grid ready, params:', params);
                    grid.current = params;
                  }}
                  onSortChanged={($event) => {
                    if ($event.source === 'uiColumnSorted') {
                      const columnState = $event.api.getColumnState();
                      const sortedColumns = columnState.filter(col => col.sort != null);

                      if (sortedColumns.length > 0) {
                        const { colId, sort } = sortedColumns[0];
                        if (colId !== sortOrder.colId || (sort === 'asc' ? 1 : -1) !== sortOrder.sortBy) {
                          setSelectedPaginationOptions((prev) => ({ ...prev, skip: 0, page: 1 }));
                          setSortOrder({ colId: colId, sortBy: sort === 'asc' ? 1 : -1 });
                        }
                      } else {
                        const defaultSort = { colId: 'createdDate', sortBy: -1 };
                        if (sortOrder.colId !== defaultSort.colId || sortOrder.sortBy !== defaultSort.sortBy) {
                          setSelectedPaginationOptions((prev) => ({ ...prev, skip: 0, page: 1 }));
                          setSortOrder(defaultSort);
                        }
                      }
                    }
                  }}
                  onFirstDataRendered={(params) => {
                    if (params.api && sortOrder.colId === 'createdDate' && sortOrder.sortBy === -1) {
                      const columnState = params.api.getColumnState().map(col => ({
                        ...col,
                        sort: null
                      }));
                      params.api.applyColumnState({
                        state: columnState,
                        defaultState: { sort: null }
                      });
                    }
                  }}
                  suppressRowTransform={true}
                />
              </SdfGrid>
            </div>
          </div>
        }
        {!tableData.length && hasNoCriteria() && <div className="no-data-text">There are currently no projects created yet. Please check back later.</div>}
        {!tableData.length && !hasNoCriteria() && <div className="no-data-text">No results found. Please try entering a different search term or switching filters for better results.</div>}

        {tableData.length > 0 && (
          <div className="flex items-center justify-end pt-4 pb-20">
            <div className="text-sm text-action-default">Rows per page:</div>
            <SdfSelect
              className="w-20 text-sm text-action-default px-4 mb-1 border-r-1 border-t-0 border-l-0 border-b-0 border-dark border-solid"
              label="rows per page"
              hideLabel={true}
              items={selectedPaginationOptions.pagination}
              onSdfChange={({ detail }) => {
                setSelectedPaginationOptions((prev) => {
                  return { ...prev, page: 1, skip: 0, pageSize: detail.value }
                });
                setOnChangePageSize(detail.value);
              }}
              value={{ label: `${selectedPaginationOptions.pageSize}`, value: selectedPaginationOptions.pageSize }}
            />
            <div className="text-sm text-action-default px-4">{`${start} - ${end} items`}</div>
            {selectedPaginationOptions.totalPages >= 1 &&
              <SdfxPagination
                currentPage={selectedPaginationOptions.page || 1}
                totalPages={selectedPaginationOptions.totalPages || 0}
                onSdfxPageChange={({ detail }) => {
                  setSelectedPaginationOptions((prev) => {
                    return { ...prev, page: detail, skip: (detail - 1) * prev.pageSize }
                  });
                  setOnChangePage(detail);
                }}
              />
            }
          </div>
        )}

        <div className={"flex-col items-center gap-8"}>
          {isBusy && (<SdfShimmer rows={10} />)}
        </div>
      </div>}
      {showSlideInModal ? <ImportLegacyProject setShowSlideInModal={setShowSlideInModal} />: null}
    </div>
  );
}
